import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../utils/AuthContext";
import Cards from "../../components/Cards";
import {
  FaCalendarCheck,
  FaFlask,
  FaFileInvoiceDollar,
  FaUserMd,
} from "react-icons/fa";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { TbSortAscending } from "react-icons/tb";
import { TiArrowRight } from "react-icons/ti";
import Calendar from "react-calendar";
import MonthlyExpensesChart from "../../components/HealthExpensesChart";
import TopDoctorCard from "../../components/TopDoctorCard";
import AppointmentBreakdownChart from "../../components/AppointmentBreakdownChart";
import AppointmentTable from "./AppointmentTable";
import { FiPlus } from "react-icons/fi";

const PatientDashboard = () => {
  const [value, setValue] = useState(new Date());
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [doctorsData, setDoctorsData] = useState([]); 
  // State for the user's filtered data
  const [myAppointments, setMyAppointments] = useState([]);
  const [labResults, setLabResults] = useState([]);
  const [pathologyBills, setPathologyBills] = useState([]);
  const [medicineBills, setMedicineBills] = useState([]);

  const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
  const BASE_URL = "https://api.muktihospital.com/api";
  const externalAppointmentURL = "https://muktihospital.com/appointment";

  useEffect(() => {
    const loadPatientData = async () => {
      if (!user || !user.id) {
        setLoading(false);
        return;
      }
      setLoading(true);

      try {
        const api = axios.create({ baseURL: BASE_URL, headers: { "x-api-key": API_KEY } });

        const patientsRes = await api.get(`/patient`);
        const allPatients = patientsRes.data || [];
        const myPatientRecord = allPatients.find(p => p.userId === user.id);
        if (!myPatientRecord) {
          console.error("Could not find a patient record for the logged-in user.");
          setLoading(false);
          return;
        }
        
        const myPatientId = myPatientRecord.id;

        const [apptRes, labRes, pathBillRes, medBillRes, doctorsRes] = await Promise.all([
          api.get(`/appointment`).catch(() => ({ data: { appointments: [] } })),
          api.get(`/pathology`).catch(() => ({ data: { results: [] } })), 
          api.get(`/pathology-bill`).catch(() => ({ data: { bills: [] } })),
          api.get(`/medicine-bill/list`).catch(() => ({ data: { bills: [] } })),
          api.get(`/doctor`).catch(() => ({ data: [] })),
        ]);

        setMyAppointments((apptRes.data.appointments || []).filter(item => item.patientId === myPatientId));
        setLabResults((labRes.data.results || labRes.data.data || []).filter(item => item.patientId === myPatientId));
        setPathologyBills((pathBillRes.data.bills || pathBillRes.data.data || []).filter(item => item.patientId === myPatientId));
        setMedicineBills((medBillRes.data.bills || medBillRes.data.data || []).filter(item => item.patientId === myPatientId));
        setDoctorsData(doctorsRes.data || []);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadPatientData();
  }, [user]);

  console.log("Doctors Data:", doctorsData);

  // --- Process the filtered data to create cards ---
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const upcomingAppointments = myAppointments.filter((a) => new Date(a.appointmentDate) >= now);
  const appointmentsCard = {
    category: "My Appointments",
    icon: <FaCalendarCheck className="text-blue-500 text-xl" />,
    total: upcomingAppointments.length,
    status: "Upcoming",
    cardListDetails: [
      { label: "Past", value: myAppointments.length - upcomingAppointments.length },
      { label: "Total", value: myAppointments.length },
    ],
  };

  const totalTestsCount = pathologyBills.reduce((count, bill) => {
    return count + (bill.tests ? bill.tests.length : 0);
  }, 0);
  const completedBills = pathologyBills.filter(bill => bill.status === 'Complete');
  const totalReportsReadyCount = completedBills.reduce((count, bill) => {
  return count + (bill.tests ? bill.tests.length : 0);
}, 0);
  const labResultsCard = {
    category: "Lab Results",
    icon: <FaFlask className="text-green-500 text-xl" />,
    total: totalReportsReadyCount,
    status: "Reports Ready",
    cardListDetails: [
      { label: "Total Tests Taken", value: totalTestsCount }
    ],
  };

  const totalPathologyDue = pathologyBills.reduce((sum, bill) => sum + (bill.dueAmount || 0), 0);
  const totalMedicineDue = medicineBills.reduce((sum, bill) => sum + (bill.dueAmount || 0), 0);
  const billsCard = {
    category: "My Bills",
    icon: <FaFileInvoiceDollar className="text-red-500 text-xl" />,
    total: `৳${(totalPathologyDue + totalMedicineDue).toFixed(2)}`,
    status: "Total Due",
    cardListDetails: [
      { label: "Pharmacy Due", value: `$${totalMedicineDue.toFixed(2)}` },
      { label: "Pathology Due", value: `$${totalPathologyDue.toFixed(2)}` },
    ],
  };
  
  const uniqueDoctors = [...new Map(myAppointments.map(item => [item.doctor?.id, item.doctor])).values()];
  const myDoctorsCard = {
    category: "My Doctors",
    icon: <FaUserMd className="text-purple-500 text-xl" />,
    total: uniqueDoctors.length,
    status: "Consulted",
    cardListDetails: [{ label: "Last Visited", value: myAppointments[0]?.doctorName || "N/A" }],
  };

  const allCards = [
    appointmentsCard,
    labResultsCard,
    billsCard,
    myDoctorsCard,
  ];

  const handleCalendarClick = (clickedDate) => {
    setValue(clickedDate);
    window.open(externalAppointmentURL, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="md:py-4">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <h1 className="font-semibold text-lg">
          Welcome back, {user?.name || "Patient"} 👋
        </h1>
        <div className="flex flex-wrap gap-4 items-center">
          <Link to="https://muktihospital.com/appointment" target="_blank" rel="noopener noreferrer">
            <button className="py-2 px-4 bg-[#73CB64] hover:bg-green-700 text-white rounded-md flex items-center gap-2 text-sm">
              <TbSortAscending /> Book Appointment
            </button>
          </Link>
        </div>
      </div>

      <div className="py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
        {loading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} height={150} />
          ))
        ) : (
          allCards.map((card, index) => <Cards key={index} data={card} showLinkIcon={false} />)
        )}
      </div>
      </div>
      {/* Main Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-y-6 xl:gap-y-0 xl:gap-6">
        {/* Calendar */}
        <div className="bg-white shadow rounded-lg">
          <div className="flex flex-wrap justify-between p-4 border-b">
            <h2 className="font-medium">My Calendar</h2>
          </div>
          <div className="py-5 sm:p-5 text-center">
            {loading ? (
              <Skeleton height={280} />
            ) : (
              <>
                <Calendar 
                  value={value} 
                  onChange={handleCalendarClick}
                  minDate={new Date()} 
                />
                <Link to="https://muktihospital.com/appointment" className="mt-4 inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded-md text-sm">
                  Book Appointment <TiArrowRight />
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Patient Stats */}
        <div className="col-span-2 bg-white shadow rounded-lg">
          <div className="flex flex-wrap gap-3 justify-between p-4 border-b">
            <h2 className="font-medium">
              My Monthly Health Expenses
            </h2>
          </div>
          <div className="md:p-4">
            {/* 2. ADD THE NEW CHART COMPONENT HERE */}
            {loading ? (
              <Skeleton height={350} />
            ) : (
              <MonthlyExpensesChart
                appointments={myAppointments}
                pathologyBills={pathologyBills}
                medicineBills={medicineBills}
              />
            )}
          </div>
        </div>
       </div>
        {/* Top Doctors & Breakdown */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-y-6 xl:gap-y-0 xl:gap-6 mt-6">
          {/* Top Doctors */}
          <div className="col-span-2 bg-white shadow rounded-lg">
            <div className="p-4 border-b">
              <h2 className="font-medium">Top Doctors</h2>
            </div>
            <div className="p-5 grid sm:grid-cols-2 lg:grid-cols-3  gap-4">
              {loading ? (
              Array.from({ length: 6 }).map((_, index) => <Skeleton key={index} height={120} />)
            ) : doctorsData.length > 0 ? (
              [...doctorsData]
                .sort((a, b) => {
                  const patientsB = parseInt(b.translations.en.patientAttended) || 0;
                  const patientsA = parseInt(a.translations.en.patientAttended) || 0;
                  if (patientsB !== patientsA) {
                    return patientsB - patientsA;
                  }
                  const experienceB = parseInt(b.translations.en.yearsOfExperience) || 0;
                  const experienceA = parseInt(a.translations.en.yearsOfExperience) || 0;
                  return experienceB - experienceA;
                })
                .slice(0, 6)
                .map((doc) => (
                  <TopDoctorCard key={doc.id} doctor={doc} isAdmin={false} />
                ))
            ) : (
              <p className="col-span-3 text-center text-gray-500 py-4">No doctors available.</p>
            )}
            </div>
            <div className="p-4 border-t">
              <Link to="https://muktihospital.com/doctor">
                <button className="px-3 py-2 bg-[#73CB64] hover:bg-M-primary-color/90 transition-all duration-300 text-white rounded-md flex items-center gap-1 text-sm">
                  See All Doctors <TiArrowRight />
                </button>
              </Link>
            </div>
          </div>
  
          {/* Patient Breakdown */}
          <div className="bg-white shadow rounded-lg">
            <div className="flex justify-between items-center flex-wrap gap-2 p-4 border-b">
              <h2 className="font-medium">Appointments by Department</h2>
              <Link to="https://muktihospital.com/doctor">
                <button className="px-3 py-2 bg-[#73CB64] hover:bg-M-primary-color/90 text-white rounded-md text-sm">
                  See All Doctors
                </button>
              </Link>
            </div>
            <div className="p-5">
              {loading ? (
                <Skeleton circle height={250} width={250} containerClassName="flex justify-center" />
              ) : myAppointments.length > 0 ? ( 
                <AppointmentBreakdownChart appointments={myAppointments} />
              ) : (
                <div className="flex items-center justify-center h-[250px] text-gray-500">
                  No appointment data found.
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="mt-6 bg-white shadow rounded-lg overflow-hidden">
        <div className="flex justify-between p-4 border-b">
          <h2 className="font-medium">All Appointments</h2>
          <Link to="https://muktihospital.com/appointment">
            <button className="px-3 py-2 bg-[#73CB64] hover:bg-M-primary-color/90 transition-all duration-300 text-white rounded-md flex items-center gap-1 text-sm">
              Add New <FiPlus />
            </button>
          </Link>
        </div>
        <AppointmentTable appointments={myAppointments} isLoading={loading} />
      </div>
    </div>
  );
};

export default PatientDashboard;